import { Font, StyleSheet } from '@react-pdf/renderer'

export enum PdfFonts {
  ROBOTO_LIGHT = 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
  ROBOTO_REGULAR = 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
  ROBOTO_MEDIUM = 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
  ROBOTO_BOLD = 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
}

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: PdfFonts.ROBOTO_LIGHT,
      fontWeight: 300,
    },
    {
      src: PdfFonts.ROBOTO_REGULAR,
      fontWeight: 400,
    },
    {
      src: PdfFonts.ROBOTO_REGULAR,
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: PdfFonts.ROBOTO_MEDIUM,
      fontWeight: 500,
    },
    {
      src: PdfFonts.ROBOTO_MEDIUM,
      fontWeight: 500,
      fontStyle: 'italic',
    },
    {
      src: PdfFonts.ROBOTO_BOLD,
      fontWeight: 700,
    },
    {
      src: PdfFonts.ROBOTO_BOLD,
      fontWeight: 700,
      fontStyle: 'italic',
    },
  ],
})

export const globalPDFStyles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
    fontFamily: 'Roboto',
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 20,
  },
  paragraphTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  text: {
    marginVertical: 2,
    fontSize: 10,
    textAlign: 'justify',
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: 'center',
    color: 'grey',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
})

export const applyInlineStylesToHTML = (html: string) => {
  return (
    html
      // Paragraphs
      .replace(/<p( style="(.*?)")?>/g, (_, __, p2: string | undefined) => {
        const baseStyle =
          'font-size: 10px; margin: 0 !important; min-height: 12px;'

        if (p2) {
          const textAlignMatch = p2.includes('text-align:')
            ? p2.match(/text-align:\s*(left|center|right|justify)/)
            : null

          const textAlign = textAlignMatch?.[1]
            ? `text-align: ${textAlignMatch[1]};`
            : ''

          return `<p style="${baseStyle} ${textAlign}">`
            .replace(/\s+/g, ' ')
            .trim()
        }
        return `<p style="${baseStyle}">`
      })
      // Heading
      .replace(
        /<h1[^>]*>/g,
        '<h1 style="font-size: 8px !important; margin: 0 !important;">'
      )
      .replace(
        /<h2[^>]*>/g,
        '<h2 style="font-size: 10px !important; margin: 0 !important;">'
      )
      .replace(
        /<h3[^>]*>/g,
        '<h3 style="font-size: 12px !important; margin: 0 !important;">'
      )
      .replace(
        /<h4[^>]*>/g,
        '<h4 style="font-size: 14px !important; margin: 0 !important;">'
      )
      .replace(
        /<h5[^>]*>/g,
        '<h5 style="font-size: 16px !important; margin: 0 !important;">'
      )
      // Bold
      .replace(/<b[^>]*>/g, '<b style="font-weight: bold; font-size: 12px;">')
      .replace(
        /<strong[^>]*>/g,
        '<strong style="font-weight: bold; font-size: 10px;">'
      )
      // remove font-size from children inside heading
      .replace(
        /<(h[1-5])[^>]*>.*?<(strong|b|span|em|i)([^>]*)>/g,
        (match, _, tag, attrs: string) => {
          const cleanedAttrs = attrs.replace(
            /font-size:\s*\d+px( !important)?;?/gi,
            ''
          )
          return match.replace(`<${tag}${attrs}>`, `<${tag}${cleanedAttrs}>`)
        }
      )
      // Italic
      // .replace(/<i [^>]*>/g, '<i style="font-style: italic; font-size: 10px;">')
      .replace(
        /<em [^>]*>/g,
        '<em style="font-style: italic; font-size: 10px;">'
      )
      // Strikethrough
      .replace(
        /<s [^>]*>/g,
        '<s style="text-decoration: line-through; font-size: 10px;">'
      )
      // Highlight
      .replace(
        /<mark[^>]*data-color="([^"]*)"[^>]*>(.*?)<\/mark>/g,
        '<span style="background-color: $1; font-size: 10px;">$2</span>'
      )
      // Lists
      .replace(
        /<ul[^>]*>/g,
        '<ul style="margin: 4px 0 4px -12px; font-size: 10px !important;">'
      )
      .replace(
        /<ol[^>]*>/g,
        '<ol style="margin: 4px 0 4px -12px; font-size: 10px !important;">'
      )
      .replace(
        /<li[^>]*>/g,
        '<li style="font-size: 10px !important; margin-bottom: 2px;">'
      )
      // Span
      .replace(/<span>/g, '<span style="font-size: 12px !important;">')
      // Tables
      .replace(
        /<table[^>]*>/g,
        '<table style="min-width: 429px; margin-top: 12px; margin-bottom: 12px;">'
      )
      // Th
      .replace(
        /<(th)[^>]*>/g,
        '<th style="border: 1px solid #adb5bd; padding: 4px; font-size: 10px; background-color: #ebebeb; margin: 0 -0.5px -1px -1px; min-height: 20px; font-weight: bold;">'
      )
      // Td
      .replace(
        /<(td)[^>]*>/g,
        '<td style="border: 1px solid #adb5bd; padding: 4px; font-size: 10px; margin: 0 -0.5px -1px -1px; min-height: 20px;">'
      )
      // Images
      .replace(/<img([^>]+)>/g, (_, attrs: string) => {
        const src = attrs.match(/src="([^"]+)"/)?.[1] || ''
        const style = attrs.match(/style="([^"]*)"/)?.[1] || ''

        const widthAttr = attrs.match(/width="(\d+)"/)?.[1]
        const widthStyle = style.match(/width:\s*(\d+px)/)?.[1]
        const width = widthAttr ? `${widthAttr}px` : widthStyle

        const hasMarginAuto = style.includes('margin: auto')
        const hasFloatRight = style.includes('float: right')
        let align = 'flex-start'

        if (hasMarginAuto) {
          align = 'center'
        }
        if (hasFloatRight) {
          align = 'flex-end'
        }

        // Monta a estrutura SEM QUEBRAR O LAYOUT
        return `
            <div style="display: flex; align-items: ${align}; width: 100%; margin: 4px 0;">
              <img src="${src}" ${width ? `style="max-width: 100%; width: ${width};"` : 'style="max-width: 100%;"'} ${attrs.replace(/style="[^"]*"/, '').replace(/width="\d+"/, '')}>
            </div>
          `
          .replace(/\s+/g, ' ')
          .trim()
      })
  )
}
